// codebaseService.js - FIXED VERSION
// This service handles all API calls related to codebases

import { apiClient } from "./api";

const codebaseService = {
  // Run code execution
  // FIXED: Added fileContent parameter to send actual CSV data
  runCode: async (
    code,
    user_id,
    codebase_id,
    type,
    data_file_path,
    dataset_id,
    file_content
  ) => {
    console.log("🚀 runCode API call:", {
      user_id,
      codebase_id,
      type,
      data_file_path,
      dataset_id,
    });

    try {
      const response = await apiClient.post("/api/run_code", {
        code,
        user_id,
        codebase_id,
        type,
        data_file_path,
        dataset_id,
        file_content,
      });

      console.log("✅ runCode API Success:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ runCode API Error:", error);
      console.error("❌ Response data:", error.response?.data);
      console.error("❌ Response status:", error.response?.status);
      throw error;
    }
  },

  // Get saved code
  getCode: async (user_id, codebase_id, framework = "tensorflow") => {
    console.log("🚀 getCode API call:", { user_id, codebase_id, framework });

    try {
      const response = await apiClient.get("/api/get_code", {
        params: { user_id, codebase_id, framework },
      });

      console.log("✅ getCode API Success");
      return response.data;
    } catch (error) {
      console.error("❌ getCode API Error:", error);
      throw error;
    }
  },

  // Save code
  saveCode: async (user_id, codebase_id, code, framework = "tensorflow") => {
    console.log("🚀 saveCode API call:", { user_id, codebase_id, framework });

    try {
      const response = await apiClient.post("/api/save_code", {
        user_id,
        codebase_id,
        code,
        framework,
      });

      console.log("✅ saveCode API Success");
      return response.data;
    } catch (error) {
      console.error("❌ saveCode API Error:", error);
      throw error;
    }
  },

  // Save a full editor session snapshot (code, chat, metrics, output) to the DB
  saveSession: async (user_id, codebase_id, session_token, session_data) => {
    console.log("🚀 saveSession API call:", { user_id, codebase_id });

    try {
      const response = await apiClient.post("/api/save_session", {
        user_id,
        codebase_id,
        session_token,
        session_data,
      });

      console.log("✅ saveSession API Success");
      return response.data;
    } catch (error) {
      console.error("❌ saveSession API Error:", error);
      throw error;
    }
  },

  // Get the saved editor session for a codebase (null if none saved yet)
  getSession: async (codebase_id) => {
    console.log("🚀 getSession API call:", { codebase_id });

    try {
      const response = await apiClient.get(`/api/get_session/${codebase_id}`);

      console.log("✅ getSession API Success");
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        console.log("ℹ️ No saved session for this codebase");
        return null;
      }
      console.error("❌ getSession API Error:", error);
      throw error;
    }
  },

  // Get plot/visualization
  getPlot: async (user_id, codebase_id) => {
    console.log("🚀 getPlot API call:", { user_id, codebase_id });

    try {
      const response = await apiClient.get("/api/get_plot", {
        params: { user_id, codebase_id },
      });

      console.log("✅ getPlot API Success");
      return response.data;
    } catch (error) {
      console.error("❌ getPlot API Error:", error);
      throw error;
    }
  },

  // Get available file paths (datasets)
  getFilepaths: async () => {
    console.log("🚀 getFilepaths API call");

    try {
      const response = await apiClient.get("/api/get_filepaths");

      console.log("✅ getFilepaths API Success");
      return response.data;
    } catch (error) {
      console.error("❌ getFilepaths API Error:", error);
      throw error;
    }
  },

  // Get single codebase by ID
  getCodebase: async (codebaseId) => {
    console.log("🚀 getCodebase API call:", { codebaseId });

    try {
      const response = await apiClient.get(`/api/get_codebase/${codebaseId}`);

      console.log("✅ getCodebase API Success");
      return response.data;
    } catch (error) {
      console.error("❌ getCodebase API Error:", error);
      throw error;
    }
  },

  // Get all codebases for a user
  getCodebasesForUser: async (userId, sessionToken) => {
    console.log("🚀 getCodebasesForUser API call:", { userId });

    try {
      // FIXED: Removed extra slash that was causing 404
      const response = await apiClient.get(
        `/api/get_codebases_for_user/${userId}/${sessionToken}`,
      );

      console.log("✅ getCodebasesForUser API Success");
      return response.data;
    } catch (error) {
      console.error("❌ getCodebasesForUser API Error:", error);
      throw error;
    }
  },

  // Add new codebase
  addCodebase: async (
    userId,
    sessionToken,
    title,
    description,
    modelType,
    framework = "tensorflow",
  ) => {
    console.log("🚀 addCodebase API call:", {
      userId,
      title,
      modelType,
      framework,
    });

    try {
      const response = await apiClient.post("/api/add_codebase", {
        userId,
        session_token: sessionToken,
        title,
        description,
        modelType,
        framework,
        geminiId: null,
        tensorflowId: null,
        pythonId: null,
      });

      console.log("✅ addCodebase API Success");
      return response.data;
    } catch (error) {
      console.error("❌ addCodebase API Error:", error);
      throw error;
    }
  },

  // Update codebase details
  changeCodebaseDetails: async (codebaseId, title, description, modelType) => {
    console.log("🚀 changeCodebaseDetails API call:", {
      codebaseId,
      title,
      modelType,
    });

    try {
      const response = await apiClient.post("/api/change_codebase_details", {
        codebase_id: codebaseId,
        title,
        description,
        modelType,
      });

      console.log("✅ changeCodebaseDetails API Success");
      return response.data;
    } catch (error) {
      console.error("❌ changeCodebaseDetails API Error:", error);
      throw error;
    }
  },

  // Get user details
  getUserDetails: async (userId, sessionToken) => {
    console.log("🚀 getUserDetails API call:", { userId });

    try {
      const response = await apiClient.get(
        `/api/get_user_details/${userId}/${sessionToken}`,
      );

      console.log("✅ getUserDetails API Success");
      return response.data;
    } catch (error) {
      console.error("❌ getUserDetails API Error:", error);
      throw error;
    }
  },

  // Delete codebase
  deleteCodebase: async (codebaseId, sessionToken) => {
    console.log("🚀 deleteCodebase API call:", { codebaseId });

    try {
      const response = await apiClient.delete(
        `/api/delete_codebase/${codebaseId}/${sessionToken}`,
      );

      console.log("✅ deleteCodebase API Success");
      return response.data;
    } catch (error) {
      console.error("❌ deleteCodebase API Error:", error);
      throw error;
    }
  },
};

export default codebaseService;
